import { Component, OnInit, Input, ViewChild, ElementRef, OnDestroy, Renderer2, OnChanges, SimpleChanges, TemplateRef, Output, EventEmitter, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { CollaborationService } from 'src/app/services/remote/collaboration.service';
import { ImageCollaborationService } from 'src/app/services/remote/image-collaboration.service';
import { Subscription, Observable, fromEvent, debounceTime } from 'rxjs';
import { Frame } from 'src/app/models/Frame';
import { ArDot } from 'src/app/models/ArDot';
import { FileShareService } from 'src/app/services/support/file-share.service';

@Component({
    selector: 'app-image-collaboration',
    templateUrl: './image-collaboration.component.html',
    styleUrls: ['./image-collaboration.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ImageCollaborationComponent implements OnInit, OnDestroy, OnChanges {

  @Input('frame') frame: Frame;
  @Input('size') size: string;
  @Input('external') external: boolean = false;
  @Input('onCrop') onCrop: boolean;
  @Output() crop = new EventEmitter<boolean>();

  @ViewChild('navbar', { static: true }) navbar:ElementRef;
  @ViewChild('scale', { static: false }) scale:ElementRef;
  @ViewChild('direction', { static: false }) direction:ElementRef;

  @ViewChild("endCollTemplate", { static: true }) private endCollTemplate: TemplateRef<any>;

  collDataSub: Subscription = null;

  annotating: boolean = false;
  annotatingSize: boolean = false;
 
  hide: boolean = true;
  isReleased: boolean = true;
  isClicked: boolean = false;
  isOnButton: boolean = false;
  annotating_view: boolean = false;
  hideForever: boolean = false;
 
  controlInput: Observable<any>;
  controlSub: Subscription = null;

  isFullscreen: boolean = false;
  fullscreenSub: Subscription = null;

  failTimeout: number = 20000;
  showWaitingAsFailed: boolean = false;
  anyUserSucceeded: boolean = false;
  notSucceededUsers: any[] = [];

  controlsOpen: boolean = false;
  showColorsDiv: boolean = false;
  showWeightsDiv: boolean = false;
  onText: boolean = false;
  showShapesDiv: boolean = false;
  onShape: boolean = false;
  showWidgetsDiv: boolean = false;
  onWidget: boolean = false;
  onPen: boolean = true;
  onMouse: boolean = false;
  onPointer: boolean = false;
  color: string = "yellow";
  weight_color: string = "transparent";
  red: string = "rgb(255, 0, 10)";
  green: string = "rgb(0, 206, 136)";
  yellow: string = "rgb(255, 195, 0)";
  white: string = "rgb(255, 255, 255)";
  borderColor: string;
  weight: string = "middle";

  image: HTMLImageElement = null;
  canvas: HTMLCanvasElement = null;
  annotationContainer: HTMLDivElement = null;

  arDots: ArDot[] = [];
  pointerCollSub: Subscription = null;

  arColorNames = {
    "dc3545": "red",
    "17a2b8": "blue",
    "28a745": "green",
    "ff9800": "orange",
    "dc35f5": "purple",
    "ffffff": "white"
  };

  constructor(
    public collaborationService: CollaborationService,
    public imageCollaborationService: ImageCollaborationService,
    private renderer: Renderer2,
    private fileShareService: FileShareService
  ) {
    this.controlInput = imageCollaborationService.getControls;
  }

  ngOnInit() {
    fromEvent(window, 'resize').pipe(debounceTime(1000)).subscribe(res => {
        this.imageCollaborationService.triggerResize();
    })
    // REMOTE_CHANGED
    this.anyUserSucceeded = true;
    this.controlsOpen = true;

    this.collDataSub = this.collaborationService.getCollaborationData(this.frame.collaborationData.id)
    .subscribe(collaborationData => {

      // annotating change
      if (!this.annotating && collaborationData.annotating) {
        this.annotating = true;
        this.toggleAnnotatingColor(this.annotating);

        if(!this.annotatingSize){
          setTimeout(() => {
            this.imageCollaborationService.triggerResize();
          },1000);
          this.annotatingSize = true;
        }
      }
      if (this.annotating && !collaborationData.annotating) {
        this.annotating = false;
        this.toggleAnnotatingColor(this.annotating);
      }
    });

    this.controlSub = this.controlInput.subscribe(control => {
      const method = control.type;
      const val = control.value;
      if(method=="hide"){
        this.hideForever = true;
      }
      else if(method=="show"){
        this.hideForever = false;
      }
      else if(method=="openControls"){
        this.controlsOpen = true;
        this.onText = false;
        this.onShape = false;
        this.onWidget = false;
        this.showShapesDiv = false;
        this.showWidgetsDiv = false;
      }
    });

    this.imageCollaborationService.startListeningControls();
    this.borderColor = "white";

    this.pointerCollSub = this.imageCollaborationService.getPointerCollaborationDots()
    .subscribe(dots => {
      this.arDots = dots.map(dot => {
        dot.color = this.arColorNames[dot.colorCode]
        return dot;
      });
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if(changes['size']) {
      this.size = changes['size'].currentValue;
      this.imageCollaborationService.changeSmall(this.size);
    }
  }

  ngOnDestroy() {
    if (this.fullscreenSub) { this.fullscreenSub.unsubscribe(); }
    if (this.collDataSub) { this.collDataSub.unsubscribe() }
    if (this.controlSub) { this.controlSub.unsubscribe() }
    if (this.pointerCollSub) { this.pointerCollSub.unsubscribe() }

    this.imageCollaborationService.endListeningControls();
  }

  onImage(img: HTMLImageElement) {
    this.image = img;
  }

  onCanvas(canvas: { canvas: HTMLCanvasElement, container: HTMLDivElement }) {
    this.canvas = canvas.canvas;
    this.annotationContainer = canvas.container;
  }

  // REMOTE_CHANGED
  createJpegFromCollaboration() {
    if (this.image && this.canvas && this.annotationContainer) {
      const cnv: HTMLCanvasElement = document.createElement("canvas");
      const width = this.canvas.width;
      const height = this.canvas.height;
  
      cnv.width = width;
      cnv.height = height;
      const ctx: CanvasRenderingContext2D = cnv.getContext('2d');
      ctx.drawImage(this.image, 0, 0, width, height);
      ctx.drawImage(this.canvas, 0, 0, width, height);
  
      const ch: HTMLCollection = this.annotationContainer.children;
      for (let i = 0; i < ch.length; i++) {
        const w = <HTMLElement>ch[i];
        if (w.classList.contains('widget')) {
          const im = <HTMLImageElement>w.firstChild;
          const x = parseFloat(im.getAttribute('data-x'));
          const y = parseFloat(im.getAttribute('data-y'));
          const length = parseFloat(im.getAttribute('data-length'));
          ctx.drawImage(im, width*x, height*y, width*length, width*length);
        }
      }

      let filename = "collaboration_edit.jpeg";
      if (this.frame.collaborationData.name.split('.').length) {
        filename = this.frame.collaborationData.name.substring(0, this.frame.collaborationData.name.length - this.frame.collaborationData.name.split('.').pop().length - 1);
        filename = filename + "_edit.jpeg";
      }
      return this.fileShareService.convertCanvasToJPEGFile(cnv, filename, 0.9)
    } else {
      return null;
    }
  }

  onLineWeightClick(){
    if(this.showWeightsDiv)
      this.weight_color = "transparent";       
    else{
      if(this.color === "red")
        this.weight_color = this.red;
      else if(this.color === "green")
        this.weight_color = this.green;
      else if(this.color === "yellow")
        this.weight_color = this.yellow;
      else if(this.color === "white")
        this.weight_color = this.white;
    }

    this.showWeightsDiv = !this.showWeightsDiv;
    // disable other tools
    this.showColorsDiv = false;   
    this.showShapesDiv = false;
    this.showWidgetsDiv = false;  

  }

  onColorClick(){
    this.showColorsDiv = !this.showColorsDiv;

    // disable other tools
    this.showWeightsDiv = false;
    this.showShapesDiv = false;
    this.showWidgetsDiv = false;
    this.weight_color = "transparent";     

  }

  onTextClick(){
    this.onText = !this.onText;
    this.imageCollaborationService.changeTextClick(this.onText);  
    this.showWeightsDiv = false;
    this.weight_color = "transparent";   
    this.showColorsDiv = false;   
    
    if(this.onText){
      // disable other tools
      this.onWidget = false;
      this.showWidgetsDiv = false;
      this.onShape = false;
      this.onPen = false;
      this.showShapesDiv = false;
      this.controlsOpen = false;
      this.onMouse = false;
      this.onPointer = false;
      

    }
    else
      this.controlsOpen = true;

  }

  onPenClick(){
    // disable other tools
    this.onText = false;
    this.showWidgetsDiv = false;
    this.onWidget = false;
    this.showShapesDiv = false;
    this.onShape = false;
    this.showWeightsDiv = false;
    this.weight_color = "transparent";   
    this.showColorsDiv = false; 
    this.onPen = !this.onPen;
    this.imageCollaborationService.changePenClick(this.onPen);
    this.controlsOpen = true;
    this.onMouse = false;
    this.onPointer = false;

  }

  onPointerClick(){
    this.onPointer = !this.onPointer;
    this.onMouse = false;
    this.onText = false;
    this.showWidgetsDiv = false;
    this.onWidget = false;
    this.showShapesDiv = false;
    this.onShape = false;
    this.showWeightsDiv = false;
    this.weight_color = "transparent";   
    this.showColorsDiv = false; 
    this.onPen = false;
    this.imageCollaborationService.changePointerClick(this.onPointer);
    this.controlsOpen = true;

  }

  onShapeClick(){
    this.showShapesDiv = !this.showShapesDiv;

    // disable other tools
    this.showWidgetsDiv = false;
    this.showWeightsDiv = false;
    this.weight_color = "transparent";   
    this.showColorsDiv = false; 
  }

  onWidgetClick(){
    this.showWidgetsDiv = !this.showWidgetsDiv;

    // disable other tools
    this.showShapesDiv = false;
    this.showWeightsDiv = false;
    this.weight_color = "transparent";   
    this.showColorsDiv = false; 
  }

  onCropClicked(){
    this.crop.emit(!this.onCrop);
    this.onMouse = false;
    this.onText = false;
    this.showWidgetsDiv = false;
    this.onWidget = false;
    this.showShapesDiv = false;
    this.onShape = false;
    this.showWeightsDiv = false;
    this.weight_color = "transparent";   
    this.showColorsDiv = false; 
    this.onPen = false;
    this.onPointer = false;
  }

  changeWeight(weight: string){
    this.imageCollaborationService.changeWeight(weight);
    this.weight = weight;  

    setTimeout(() => {
      this.weight_color = "transparent";   
      this.showWeightsDiv = false;
    },300);


  }

  changeColor(color: string){
    this.imageCollaborationService.changeColor(color);
    this.color = color;
    if(this.color === "red")
      this.borderColor = this.red;
    else if(this.color === "green")
      this.borderColor = this.green;
    else if(this.color === "yellow")
      this.borderColor = this.yellow;
    else if(this.color === "white")
      this.borderColor = this.white;

    setTimeout(() => {
      this.showColorsDiv = false; 
    },300);

    this.renderer.setStyle(this.frame.nativeElement, 'border', '2px solid '+this.borderColor);
   
  }

  changeShape(shape: string){
    // disable other tools
    this.controlsOpen = false;
    this.showShapesDiv = false;
    this.onPen = false;
    this.onWidget = false;
    this.onText = false;
    this.onMouse = false;
    this.onPointer = false;


    this.imageCollaborationService.changeShape(shape); 
    this.onShape = true;
  }

  
  changeWidget(widget: string){

    // disable other tools
    this.controlsOpen = false;
    this.showWidgetsDiv = false;
    this.onPen = false;
    this.onShape = false;
    this.onText = false;
    this.onMouse = false;
    this.onPointer = false;

    this.imageCollaborationService.changeShape(widget);
    this.onWidget = true;
  }

  onMouseClick(){
    this.onMouse = !this.onMouse;
    this.onText = false;
    this.showWidgetsDiv = false;
    this.onWidget = false;
    this.showShapesDiv = false;
    this.onShape = false;
    this.showWeightsDiv = false;
    this.weight_color = "transparent";   
    this.showColorsDiv = false; 
    this.onPen = false;
    this.imageCollaborationService.changeMouseClick(this.onMouse);
    this.controlsOpen = true;
    this.onPointer = false;

  }

  onDocumentMouseUp(e){
    this.isClicked = false;
    this.isReleased = true;
    
  }
 
  onDocumentMouseDown(e){
    this.isClicked = true;
    this.isReleased = false;
  }

  onDocumentMouseMove(e){

    if(this.onShape || this.onText || this.onWidget){
      this.hide = true;
      this.controlsOpen = false;
      //this.navbar.nativeElement.style["margin-top"]="-40px";
      return;
    }
  
    if(this.isClicked && !this.isReleased && !this.isOnButton && this.annotating){

      this.navbar.nativeElement.style.transition="margin 0s";
      if (this.controlsOpen) {
        this.scale.nativeElement.style.transition="margin 0s";
        this.direction.nativeElement.style.transition="margin 0s";
      }

      this.hide = true;
    }
    else{
      if(!this.hideForever){
        this.navbar.nativeElement.style.transition="margin 0.5s ";
        if (this.controlsOpen) {
          this.scale.nativeElement.style.transition="margin 0.5s ease-out";
          this.direction.nativeElement.style.transition="margin 0.5s";
        }

        this.hide = false;
      }
      else{
        this.navbar.nativeElement.style.transition="margin 0s";
        if (this.controlsOpen) {
          this.scale.nativeElement.style.transition="margin 0s";
          this.direction.nativeElement.style.transition="margin 0s";
        }
        this.hide = true;
      }
    }    
  }

  onDocumentMouseLeave(e){
    this.isClicked = false;
    this.isReleased = true;
    this.hide = true;
  }
  onDocumentMouseEnter(e){
    if (this.size !== 'small')
      this.hide = false;
  }
  onDocumentMouseLeaveControl(e){
    this.isOnButton = false;
  }
  onDocumentMouseEnterControl(e){
    this.isOnButton = true;
  }
   
  annotate() {
    this.toggleAnnotatingColor(!this.annotating);
    this.collaborationService.toggleAnnotate(!this.annotating);
    this.imageCollaborationService.toggleAnnotate(!this.annotating);
    setTimeout(() => {
      this.imageCollaborationService.triggerResize();
    },100);
    if(!this.annotating){
      this.showColorsDiv = false;  
      this.showWeightsDiv = false; 
      this.showWidgetsDiv = false;
      this.showShapesDiv = false;
      this.onShape = false;
      this.onWidget = false;
      this.onPen = true;  
      this.onMouse = false;
      this.onPointer = false;
      this.weight_color = "transparent";  
      this.onText = false;
      this.imageCollaborationService.changePenClick(this.onPen);
    }  
   
  }

  toggleAnnotatingColor(annotating: boolean) {
    if (annotating) {
      if(this.color === "red")
        this.borderColor = this.red;
      else if(this.color === "green")
        this.borderColor = this.green;
      else if(this.color === "yellow")
        this.borderColor = this.yellow;
      else if(this.color === "white"){
        this.borderColor = this.white;
      }
      this.renderer.setStyle(this.frame.nativeElement, 'border', '2px solid '+this.borderColor);


    } else {
      this.renderer.setStyle(this.frame.nativeElement, 'border', '2px solid white');
      this.borderColor = "white";
    }
  }

  undo(){
    
    this.dismissTools();

    // trigger undo function.
    this.imageCollaborationService.undo();


  }
  redo(){
   
    this.dismissTools();

    // trigger redo function.
    this.imageCollaborationService.redo();

 
  }
  zoomIn(){

    this.dismissTools();

    // trigger zoom In function.
    this.imageCollaborationService.zoomIn();


  }
  zoomOut(){

    this.dismissTools();
    
    // trigger zoom Out function.
    this.imageCollaborationService.zoomOut();
   
  }
  moveUp(){

   this.dismissTools();
    
    // trigger moveUp function.
    this.imageCollaborationService.moveUp("mouse");

  }
  moveRight(){

    this.dismissTools();
    
    // trigger moveRight function.
    this.imageCollaborationService.moveRight("mouse");

  }
  moveDown(){
   
    this.dismissTools();
    
    // trigger moveDown function.
    this.imageCollaborationService.moveDown("mouse");

  }
  moveLeft(){
    
    this.dismissTools();
    
    // trigger moveLeft function.     
    this.imageCollaborationService.moveLeft("mouse");
    this.showShapesDiv = false;
    this.showWidgetsDiv = false;
  }
  refresh(){
     
    this.dismissTools();    
    this.onText = false;
    this.onShape = false;
    this.onPen = true;
    this.onMouse = false;
    this.onPointer = false;
    this.imageCollaborationService.revert();

  }

  dismissTools(){

    // when a control button is clicked, dissmiss the tools div.
    this.showColorsDiv = false; 
    this.showWeightsDiv = false;  
    this.showShapesDiv = false;
    this.showWidgetsDiv = false;
    this.weight_color = "transparent";       
    this.hide = false;

  }
  onDocumentTouchStart(e){
  }
  onDocumentTouchMove(e){
  }
  onDocumentTouchEnd(e){
  }

//   @HostListener('window:resize', ['$event'])
//    onResize(event) {
//      if(event.target.innerWidth <= 992){
//       setTimeout(() => {
//         this.imageCollaborationService.triggerResize();
//       },100);
//      }
// }
}
